import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AcceptInvite() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [state, setState] = useState("loading"); // loading | valid | invalid
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Accept Invitation | ImpactVest";
    (async () => {
      try {
        const res = await api.get(`/auth/invite/${token}`);
        if (res.data.valid) {
          setInvite(res.data);
          setState("valid");
        } else {
          setState("invalid");
        }
      } catch {
        setState("invalid");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 12) return toast.error("Password must be at least 12 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setSubmitting(true);
    try {
      const res = await api.post("/auth/accept-invite", { token, password });
      login(res.data.token, res.data.user);
      toast.success("Welcome to ImpactVest.");
      window.location.assign("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to accept invitation.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Header />
      <main className="pt-28 md:pt-32 pb-24 min-h-[70vh]">
        <div className="iv-container max-w-2xl">
          <div className="iv-eyebrow mb-8">Workspace invitation</div>
          {state === "loading" && (
            <p className="font-mono text-[12px] uppercase tracking-widest text-iv-fg-3">Verifying invitation…</p>
          )}
          {state === "invalid" && (
            <>
              <h1 className="font-serif-display text-[40px] md:text-[52px] leading-tight text-iv-fg">
                This invitation is no longer valid.
              </h1>
              <p className="mt-6 text-[15px] text-iv-fg-2">
                It may have expired or already been used. You can create a new
                private workspace using the self-service access route.
              </p>
              <div className="mt-8 flex gap-3">
                <Link to="/access" className="iv-btn iv-btn--primary">Analyse your evidence</Link>
                <Link to="/login" className="iv-btn">Sign in</Link>
              </div>
            </>
          )}
          {state === "valid" && (
            <>
              <h1 className="font-serif-display text-[40px] md:text-[52px] leading-tight text-iv-fg">
                Welcome, {invite.full_name}.
              </h1>
              <p className="mt-4 text-[15px] text-iv-fg-2">
                You&rsquo;ve been invited to join <strong className="text-iv-fg">{invite.organization}</strong> on
                ImpactVest. Set a password to activate your account.
              </p>
              <form onSubmit={submit} className="mt-10 iv-panel p-8">
                <div className="iv-field mb-6">
                  <span>Professional Email</span>
                  <input value={invite.email} disabled className="opacity-70" />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <label className="iv-field">
                    <span>Password (min 12 characters)</span>
                    <input type="password" required minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Choose a strong password" />
                  </label>
                  <label className="iv-field">
                    <span>Confirm Password</span>
                    <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" />
                  </label>
                </div>
                <button type="submit" disabled={submitting} className="iv-btn iv-btn--primary mt-8">
                  {submitting ? "Activating…" : "Activate Account"}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
